import { useQuery } from "@tanstack/react-query";

import useApiClient from "@/hooks/useApiClient";
import { QUERY_OPTIONS_LOAD_ONCE } from "@/apiUtils";

// /data_asset/search rejects page sizes above 500 with a 422.
const SEARCH_PAGE_LIMIT = 500;

/**
 * Batch-resolves data-asset public IDs to file paths with one
 * searchDataAssets call per page of the (deduplicated, sorted) ID set. The
 * sorted-IDs query key means identical lists across renders/panels share one
 * cache entry instead of fanning out duplicate lookups.
 */
export function useAssetPathsByPublicId(
  publicIds: string[]
): Map<string, string | undefined> {
  const api = useApiClient();
  const uniqueIds = [...new Set(publicIds)].sort();

  const { data } = useQuery({
    queryKey: ["storageOps", "assetPaths", uniqueIds],
    queryFn: async () => {
      const pages = [];
      for (let i = 0; i < uniqueIds.length; i += SEARCH_PAGE_LIMIT) {
        pages.push(uniqueIds.slice(i, i + SEARCH_PAGE_LIMIT));
      }
      const results = await Promise.all(
        pages.map((page) =>
          api.dataAccess.searchDataAssets({
            dataAssetPublicIds: page,
            size: page.length,
          })
        )
      );
      return results.flatMap((result) => result.items ?? []);
    },
    enabled: uniqueIds.length > 0,
    ...QUERY_OPTIONS_LOAD_ONCE,
    // Old jobs can reference since-deleted assets; fall back to the raw ID
    // instead of blowing up the page.
    throwOnError: false,
  });

  return new Map(
    (data ?? [])
      .filter(
        (asset): asset is typeof asset & { public_id: string } =>
          typeof asset.public_id === "string"
      )
      .map((asset) => [asset.public_id, asset.path ?? undefined])
  );
}

/**
 * Renders sample data-asset public IDs as human-readable file paths.
 * Falls back to the raw ID (kept in the tooltip regardless) for anything
 * that can't be resolved.
 */
function AssetPathList({
  publicIds,
  pathByPublicId,
  label = "Sample assets:",
}: {
  publicIds: string[];
  pathByPublicId: Map<string, string | undefined>;
  label?: string;
}) {
  if (publicIds.length === 0) {
    return null;
  }

  return (
    <div className="mt-1">
      {label && <p>{label}</p>}
      <ul className="mt-1 list-disc pl-5">
        {publicIds.map((publicId) => (
          <li key={publicId} className="break-all" title={publicId}>
            {pathByPublicId.get(publicId) ?? publicId}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default AssetPathList;
